<template>
  <div class="sc-page">
    <div class="sc-page-inner">
      <!-- Top header (minimal) -->
      <header class="sc-header">
        <div class="d-flex justify-content-between align-items-end flex-wrap gap-3">
          <div>
            <div class="sc-kicker">WORKFLOW Repo</div>
            <h1 class="sc-title mb-2">{{ $t("home.title") }}</h1>
            <p class="sc-subtitle mb-0">
              {{ $t("home.subtitle") }}
            </p>
          </div>

          <div class="d-flex gap-2">
            <button class="btn btn-sm btn-outline-secondary" @click="refreshRandom">
              {{ $t("home.refresh") }}
            </button>
            <button class="btn btn-sm btn-outline-primary" @click="goBatch">
              {{ $t("home.goBatch") }}
            </button>
          </div>
        </div>
      </header>

      <!-- Main featured: Science-style Hero (1 image = 1 workflow) -->
      <section class="sc-hero">
        <div class="sc-hero-shell">
          <div class="sc-hero-media">
            <img
              :src="activeHero.cover_url || activeHero.cover || defaultCover"
              :alt="activeHero.title"
              loading="lazy"
            />
            <div class="sc-hero-vignette"></div>

            <button class="sc-hero-nav sc-hero-nav--left" @click="heroPrev" aria-label="Prev">‹</button>
            <button class="sc-hero-nav sc-hero-nav--right" @click="heroNext" aria-label="Next">›</button>
          </div>

          <div class="sc-hero-overlay">
            <div class="sc-hero-overlay-inner">
              <div class="sc-hero-meta">
                <span class="sc-pill">{{ activeHero.badge || "FEATURED" }}</span>
                <span class="sc-sep">•</span>
                <span class="sc-meta-text">{{ activeHero.domain || "General" }}</span>
                <span class="sc-sep">•</span>

                <span class="sc-meta-text">
                  steps {{ typeof activeHero.steps_count === "number" ? activeHero.steps_count : (activeHero.steps || []).length }}
                </span>

                <span class="sc-sep">•</span>
                <span class="sc-meta-text">
                  inputs {{ summarizeKeys(activeHero.input_keys || activeHero.inputs) }}
                </span>
              </div>

              <div class="sc-hero-title" @click="openPreset(activeHero)">
                {{ activeHero.title }}
              </div>

              <div class="sc-hero-dek">
                {{ activeHero.desc }}
              </div>

              <div class="sc-hero-actions">
                <button class="btn btn-sm btn-dark" @click="openPreset(activeHero)">View / Apply</button>
                <button class="btn btn-sm btn-outline-dark" @click="copyText(activeHero.share_code)">Copy share code</button>
                <span class="sc-code">{{ activeHero.share_code }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="sc-llm sc-llm--featured">
        <div class="sc-llm-shell">
          <div class="sc-llm-badge">LLM</div>

          <div class="sc-block sc-block--accent">
            <div class="sc-block-head">
              <div class="d-flex justify-content-between align-items-end flex-wrap gap-2">
                <div>
                  <div class="sc-block-title sc-block-title--accent">{{ $t("home.llmTitle") }}</div>
                  <div class="sc-block-subtitle">
                    {{ $t("home.llmSubtitle") }}
                  </div>
                </div>
                <div class="sc-llm-mini">
                  <span class="sc-pill sc-pill--accent">NATURAL → PRESET</span>
                </div>
              </div>
            </div>

            <div class="row g-3 align-items-end">
              <div class="col-12 col-lg-9">
                <textarea
                  v-model="llmPrompt"
                  class="form-control form-control-sm sc-llm-textarea"
                  rows="3"
                  placeholder="Generate a pair-end WGS analysis pipeline, use FastQC for quality control, remove standard Illumina adapters, and use the mouse reference genome..."
                ></textarea>
              </div>
              <div class="col-12 col-lg-3 d-flex gap-2">
                <button class="btn btn-sm btn-outline-secondary w-100" @click="fillPromptDemo">Example</button>
                <button
                  class="btn btn-sm btn-dark w-100"
                  @click="generatePreset"
                  :disabled="!llmPrompt.trim() || llmState==='running'"
                  :title="!llmPrompt.trim() ? 'Please enter a requirement description first.' : ''"
                >
                  <span v-if="llmState==='running'">running…</span>
                  <span v-else>Generate</span>
                </button>
                <button
                  v-if="llmState==='running'"
                  class="btn btn-sm btn-outline-danger w-100"
                  @click="cancelGenerate"
                >
                  Cancel
                </button>
              </div>
            </div>

            <div class="sc-hint mt-2">
              <div class="mt-3" v-if="llmState!=='idle'">
                <!-- running -->
                <div v-if="llmState==='running'" class="sc-hint">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <div><strong>LLM Generating:</strong> {{ llmStageText }}</div>
                    <div class="text-muted" style="font-size:12px;">{{ Math.round(llmProgress) }}%</div>
                  </div>

                  <div class="progress" style="height: 8px;">
                    <div class="progress-bar" role="progressbar" :style="{ width: llmProgress + '%' }"></div>
                  </div>

                  <div v-if="llmLongWait" class="text-muted mt-2" style="font-size:12px;">
                    The model is currently busy and the wait has been longer than expected. You may continue waiting or click Cancel to retry.
                  </div>
                </div>

                <!-- done -->
                <div v-else-if="llmState==='done'" class="sc-hint">
                  <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div>
                      <strong>Generation Complete</strong>
                      <div class="text-muted" style="font-size:12px;">{{ llmStageText }}</div>
                    </div>

                    <div class="d-flex gap-2">
                      <button class="btn btn-sm btn-dark" @click="applyLlmResult" :disabled="!llmResult?.share_code">
                        {{ $t("home.viewApply") }}
                      </button>
                      <button
                        class="btn btn-sm btn-outline-dark"
                        @click="copyText(llmResult?.share_code || '')"
                        :disabled="!llmResult?.share_code"
                      >
                        {{ $t("home.copyCode") }}
                      </button>
                    </div>
                  </div>

                  <!-- Share code (highlighted) -->
                  <div class="mt-2">
                    <div class="d-flex align-items-center gap-2 mb-1">
                      <span class="text-muted" style="font-size:12px;">Share Code</span>
                    </div>

                    <span class="sc-code sc-code--big">
                      {{ llmResult?.share_code || '—' }}
                    </span>
                  </div>

                  <!-- Explanation (code-block style, not collapsed) -->
                  <div class="mt-3">
                    <div class="text-muted mb-1" style="font-size:12px;">Explanation</div>
                    <pre class="sc-llm-explain">
{{ normalizeExplanation(llmResult?.explanation) }}
                    </pre>
                  </div>
                </div>

                <!-- error -->
                <div v-else-if="llmState==='error'" class="sc-hint">
                  <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div>
                      <strong>Generation Failed</strong>
                      <div class="text-danger mt-1" style="font-size:12px;">{{ llmError }}</div>
                    </div>
                    <div class="d-flex gap-2">
                      <button class="btn btn-sm btn-dark" @click="generatePreset" :disabled="!llmPrompt.trim()">Retry</button>
                      <button class="btn btn-sm btn-outline-secondary" @click="resetLlmState">Close</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <!-- Recommended sections: no images, big titles + small abstracts -->
      <section class="sc-sections">
        <div class="row g-4">
          <!-- Left: Trending -->
          <div class="col-12 col-xl-7">
            <div class="sc-block">
              <div class="sc-block-head">
                <div class="sc-block-title">Trending</div>
                <div class="sc-block-subtitle">Recently Popular Preset Bioinformatics Pipelines</div>
              </div>

              <div class="sc-list">
                <article
                  v-for="(p, idx) in trendingPresets"
                  :key="p.id || p.share_code"
                  class="sc-list-item"
                  :class="{ 'is-lead': idx === 0 }"
                  @click="openPreset(p)"
                  role="button"
                  tabindex="0"
                >
                  <div class="sc-item-kicker">
                    <span class="sc-item-tag">{{ p.domain || "Workflow" }}</span>
                    <span class="sc-item-dot">•</span>

                    <span class="sc-item-meta">
                      steps {{ typeof p.steps_count === "number" ? p.steps_count : (p.steps || []).length }}
                    </span>

                    <span class="sc-item-dot">•</span>

                    <span class="sc-item-meta">
                      {{ summarizeKeys(p.input_keys || p.inputs) }}
                    </span>
                  </div>

                  <div class="sc-list-title">{{ p.title }}</div>
                  <div class="sc-list-dek">{{ p.desc }}</div>

                  <div class="sc-list-meta">
                    <span class="sc-meta-chip">
                      {{ summarizePipeline(p.pipeline || p.steps) }}
                    </span>
                    <span class="sc-meta-code">{{ p.share_code }}</span>
                  </div>
                </article>
              </div>
            </div>
          </div>

          <!-- Right: Recommended + Quick Apply -->
          <div class="col-12 col-xl-5">
            <div class="sc-block">
              <div class="sc-block-head d-flex justify-content-between align-items-end">
                <div>
                  <div class="sc-block-title">Recommended</div>
                  <div class="sc-block-subtitle">Random Discovery & Admin Recommendations</div>
                </div>
                <button class="btn btn-sm btn-outline-secondary" @click="refreshRandom">Refresh</button>
              </div>

              <div class="sc-mini-list">
                <div
                  v-for="p in randomPresets"
                  :key="p.id || p.share_code"
                  class="sc-mini-item"
                  @click="openPreset(p)"
                  role="button"
                  tabindex="0"
                >
                  <div class="sc-mini-kicker">
                    <span class="sc-mini-tag">{{ p.domain || "Workflow" }}</span>
                    <span class="sc-item-dot">•</span>

                    <span class="sc-mini-meta">
                      steps {{ typeof p.steps_count === "number" ? p.steps_count : (p.steps || []).length }}
                    </span>
                  </div>

                  <div class="sc-mini-title">{{ p.title }}</div>
                  <div class="sc-mini-dek">{{ p.desc }}</div>

                  <div class="sc-mini-footer">
                    <span class="sc-mini-soft">
                      {{ summarizePipeline(p.pipeline || p.steps) }}
                    </span>
                    <span class="sc-mini-code">{{ p.share_code }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Apply -->
            <div class="sc-block mt-4">
              <div class="sc-block-head">
                <div class="sc-block-title">Quick Apply</div>
                <div class="sc-block-subtitle">{{ $t("home.quickApplySub") }}</div>
              </div>

              <div class="d-flex gap-2">
                <input
                  v-model="presetKeyword"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Paste a share code (p:...) or enter a preset name"
                  @keyup.enter="applyPreset"
                />
                <button class="btn btn-sm btn-outline-primary" @click="applyPreset" :disabled="!presetKeyword.trim()">
                  {{ $t("home.apply") }}
                </button>
              </div>

              <div class="sc-hint mt-2">
                Supported formats: Share Code (p:...), Preset Name, and JSON
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Search -->
      <section class="sc-search">
        <div class="sc-block">
          <div class="sc-block-head">
            <div class="sc-block-title">Search</div>
            <div class="sc-block-subtitle">Search presets by title, abstract, software name, or input keywords</div>
          </div>

          <div class="row g-2 align-items-end">
            <div class="col-12 col-lg-6">
              <label class="sc-form-label">Keywords</label>
              <input
                v-model="searchQuery"
                class="form-control form-control-sm"
                placeholder="e.g., STAR / bwa / fq1:sample / markdup"
              />
            </div>
            <div class="col-12 col-lg-3">
              <label class="sc-form-label">Field</label>
              <select v-model="searchDomain" class="form-select form-select-sm">
                <option value="">All</option>
                <option v-for="d in domainOptions" :key="d" :value="d">{{ d }}</option>
              </select>
            </div>
            <div class="col-12 col-lg-3 d-flex gap-2">
              <button class="btn btn-sm btn-outline-secondary w-100" @click="clearSearch">Clear</button>
              <button class="btn btn-sm btn-dark w-100" @click="jumpToFirstResult" :disabled="filteredPresets.length===0">
                Open the first result
              </button>
            </div>
          </div>

          <div class="sc-search-results mt-3">
            <div class="sc-search-viewport">
              <div v-if="filteredPresets.length === 0" class="sc-empty">
                No presets matched your search. Try using shorter keywords (e.g., bwa, star, fq1).
              </div>

              <div v-else class="sc-grid">
                <div
                  v-for="p in filteredPresets"
                  :key="p.id || p.share_code"
                  class="sc-result"
                  @click="openPreset(p)"
                  role="button"
                  tabindex="0"
                >
                  <div class="sc-result-kicker">
                    <span class="sc-result-tag">{{ p.domain || "Workflow" }}</span>
                    <span class="sc-item-dot">•</span>

                    <span class="sc-result-meta">
                      steps {{ typeof p.steps_count === "number" ? p.steps_count : (p.steps||[]).length }}
                    </span>
                  </div>

                  <div class="sc-result-title">{{ p.title }}</div>
                  <div class="sc-result-dek">{{ p.desc }}</div>

                  <div class="sc-result-footer">
                    <span class="sc-result-soft">{{ summarizePipeline(p.pipeline || p.steps) }}</span>
                    <span class="sc-result-code">{{ p.share_code }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      <div class="py-3"></div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import API_BASE_URL from "@/api";

export default {
  name: "PresetStoreHome",
  props: ["taskId"],
  data() {
    return {
      llmState: "idle",   // idle | running | done | error
      llmProgress: 0,
      llmStageText: "",
      llmError: "",
      llmResult: null,
      llmTimer: null,
      llmStartedAt: 0,
      llmLongWait: false,
      llmAbort: null,     // AbortController

      presetKeyword: "",
      llmPrompt: "",
      heroIndex: 0,

      // Search
      searchQuery: "",
      searchDomain: "",
      searchResults: [],            // backend search results
      searchDebounceTimer: null,
      domainOptionsFromBackend: [], // backend domains

      // Fallback mock presets when backend is unavailable
      presets: this.buildMockPresets(),

      // Home blocks (backend)
      heroPresets: [],
      trendingFromBackend: [],
      randomPresets: [],

      defaultCover: "/preset_covers/demo1.png",
    };
  },
  computed: {
    featuredPresets() {
      if (Array.isArray(this.heroPresets) && this.heroPresets.length > 0) return this.heroPresets;
      return this.presets.filter(p => p.featured);
    },
    activeHero() {
      const list = this.featuredPresets;
      if (!list.length) {
        return {
          id: "empty",
          title: "No featured preset",
          desc: "Please configure featured (Hero) presets first.",
          cover_url: this.defaultCover,
          cover: this.defaultCover,
          steps_count: 0,
          steps: [],
          input_keys: [],
          inputs: [],
          share_code: "p:empty",
          domain: "General",
        };
      }
      const idx = Math.max(0, Math.min(this.heroIndex, list.length - 1));
      return list[idx];
    },

    trendingPresets() {
      if (Array.isArray(this.trendingFromBackend) && this.trendingFromBackend.length > 0) {
        return this.trendingFromBackend;
      }
      return this.presets
        .filter(p => !p.featured)
        .slice()
        .sort((a, b) => (b.score || 0) - (a.score || 0))
        .slice(0, 6);
    },

    domainOptions() {
      if (Array.isArray(this.domainOptionsFromBackend) && this.domainOptionsFromBackend.length > 0) {
        return this.domainOptionsFromBackend;
      }
      const set = new Set(this.presets.map(p => p.domain).filter(Boolean));
      return Array.from(set).sort();
    },

    filteredPresets() {
      if (Array.isArray(this.searchResults) && this.searchResults.length > 0) return this.searchResults;

      const q = (this.searchQuery || "").trim().toLowerCase();
      const d = (this.searchDomain || "").trim();

      const match = (p) => {
        if (d && p.domain !== d) return false;
        if (!q) return true;

        const hay = [
          p.title,
          p.desc,
          p.share_code,
          (p.inputs || []).map(x => x.key).join(" "),
          (p.steps || []).map(s => `${s.software || ""} ${s.feature || ""}`).join(" "),
        ].filter(Boolean).join(" ").toLowerCase();

        return hay.includes(q);
      };

      return this.presets
        .filter(match)
        .slice()
        .sort((a, b) => (b.score || 0) - (a.score || 0));
    },
  },

  watch: {
    searchQuery() { this.queueSearch(); },
    searchDomain() { this.queueSearch(); },
  },

  mounted() {
    this.loadHomeBlocks();
    this.refreshRandom();
    this.queueSearch(true);
  },

  methods: {
    // If backend messages include CJK characters, replace them with an English fallback
    sanitizeToEnglish(msg, fallback = "Something went wrong.") {
      const s = (msg ?? "").toString().trim();
      if (!s) return fallback;
      const hasCJK = /[\u4e00-\u9fff]/.test(s);
      return hasCJK ? fallback : s;
    },

    normalizeExplanation(explanation) {
      if (!explanation) return "—";
      if (typeof explanation === "string") return explanation.trim() || "—";
      if (Array.isArray(explanation)) return explanation.filter(Boolean).join("\n\n");
      try { return JSON.stringify(explanation, null, 2); } catch (e) { return String(explanation); }
    },

    resetLlmState() {
      if (this.llmTimer) { clearInterval(this.llmTimer); this.llmTimer = null; }
      if (this.llmAbort) { try { this.llmAbort.abort(); } catch (_) { console.log("abort failed"); } }
      this.llmAbort = null;

      this.llmState = "idle";
      this.llmProgress = 0;
      this.llmStageText = "";
      this.llmError = "";
      this.llmResult = null;
      this.llmStartedAt = 0;
      this.llmLongWait = false;
    },

    startFakeProgress() {
      const timeline = [
        { t: 0,    p: 8,  s: "Parsing requirements…" },
        { t: 2000, p: 28, s: "Assembling pipeline steps…" },
        { t: 6000, p: 62, s: "Validating inputs, outputs, and dependencies…" },
        { t: 9000, p: 82, s: "Generating share code and persisting data…" },
      ];

      const start = Date.now();
      this.llmTimer = setInterval(() => {
        const elapsed = Date.now() - start;
        this.llmLongWait = (Date.now() - this.llmStartedAt) > 15000;

        for (let i = timeline.length - 1; i >= 0; i--) {
          if (elapsed >= timeline[i].t) {
            this.llmStageText = timeline[i].s;
            this.llmProgress = Math.max(this.llmProgress, timeline[i].p);
            break;
          }
        }

        if (this.llmProgress < 92) this.llmProgress += 1.2;
        if (this.llmProgress > 92) this.llmProgress = 92;
      }, 350);
    },

    stopFakeProgress() {
      if (this.llmTimer) { clearInterval(this.llmTimer); this.llmTimer = null; }
    },

    async generatePreset() {
      const prompt = (this.llmPrompt || "").trim();
      if (!prompt) return;
      if (this.llmState === "running") return;

      this.llmState = "running";
      this.llmProgress = 2;
      this.llmStageText = "Submitting request…";
      this.llmError = "";
      this.llmResult = null;
      this.llmStartedAt = Date.now();
      this.llmLongWait = false;

      this.llmAbort = new AbortController();
      this.startFakeProgress();

      try {
        const res = await axios.post(
          `${API_BASE_URL}/api/biology/llm_generate_json/`,
          { prompt },
          { signal: this.llmAbort.signal }
        );
        const data = res.data || {};
        this.stopFakeProgress();
        this.llmProgress = 100;
        this.llmStageText = "OK";
        this.llmState = "done";
        this.llmResult = data.result || data.data || data;
      } catch (e) {
        this.stopFakeProgress();

        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
          this.resetLlmState();
          return;
        }

        console.error("generatePreset failed:", e);
        this.llmState = "error";

        const rawMsg =
          e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.message ||
          "";

        this.llmError = this.sanitizeToEnglish(rawMsg, "LLM generation failed.");
      } finally {
        this.llmAbort = null;
      }
    },

    cancelGenerate() {
      if (this.llmAbort) { try { this.llmAbort.abort(); } catch (_) { console.log("abort failed"); } }
      this.resetLlmState();
    },

    applyLlmResult() {
      const code = this.llmResult?.share_code;
      if (!code) return;
      this.$router.push({ name: "BatchTask", params: { taskId: this.taskId }, query: { preset: code } });
    },

    async loadHomeBlocks() {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/biology/presets_home/`);
        const data = res.data || {};

        this.heroPresets = Array.isArray(data.hero) ? data.hero : [];
        this.trendingFromBackend = Array.isArray(data.trending) ? data.trending : [];

        if (Array.isArray(data.domains)) {
          this.domainOptionsFromBackend = data.domains.slice().filter(Boolean);
        }

        if (Array.isArray(data.recommended) && data.recommended.length > 0) {
          this.randomPresets = data.recommended;
        }

        this.heroIndex = 0;
      } catch (e) {
        console.error("loadHomeBlocks failed:", e);
      }
    },

    async refreshRandom() {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/biology/presets_recommended/`, { params: { limit: 5 } });
        const data = res.data || {};
        const list = data.recommended || data.results || data.items || data || [];
        if (Array.isArray(list) && list.length > 0) {
          this.randomPresets = list;
          return;
        }
        this.refreshRandomFallback();
      } catch (e) {
        console.error("refreshRandom failed:", e);
        this.refreshRandomFallback();
      }
    },

    refreshRandomFallback() {
      const pool = this.presets.filter(p => !p.featured);
      const shuffled = [...pool].sort(() => Math.random() - 0.5);
      this.randomPresets = shuffled.slice(0, 5);
    },

    queueSearch(immediate = false) {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      const delay = immediate ? 0 : 350;
      this.searchDebounceTimer = setTimeout(() => this.performSearch(), delay);
    },

    async performSearch() {
      const q = (this.searchQuery || "").trim();
      const domain = (this.searchDomain || "").trim();

      try {
        const res = await axios.get(`${API_BASE_URL}/api/biology/presets_search/`, {
          params: { q, domain, limit: 30 },
        });
        const data = res.data || {};
        const list = data.results || data.items || data.data || data || [];
        this.searchResults = Array.isArray(list) ? list : [];
      } catch (e) {
        console.error("performSearch failed:", e);
        this.searchResults = [];
      }
    },

    summarizeKeys(inputsOrKeys) {
      if (!inputsOrKeys) return "—";
      if (Array.isArray(inputsOrKeys) && typeof inputsOrKeys[0] === "string") {
        return inputsOrKeys.slice(0, 4).join(", ");
      }
      if (Array.isArray(inputsOrKeys)) {
        return inputsOrKeys.map(x => x?.key).filter(Boolean).slice(0, 4).join(", ") || "—";
      }
      return "—";
    },

    summarizePipeline(pipelineOrSteps) {
      if (!pipelineOrSteps) return "pipeline";
      if (Array.isArray(pipelineOrSteps) && typeof pipelineOrSteps[0] === "string") {
        const list = pipelineOrSteps.filter(Boolean);
        if (!list.length) return "pipeline";
        const head = list.slice(0, 4).join(" → ");
        return list.length > 4 ? `${head} → …` : head;
      }
      const list = (pipelineOrSteps || []).map(s => s?.software).filter(Boolean);
      if (!list.length) return "pipeline";
      const head = list.slice(0, 4).join(" → ");
      return list.length > 4 ? `${head} → …` : head;
    },

    heroPrev() {
      const n = this.featuredPresets.length || 1;
      this.heroIndex = (this.heroIndex - 1 + n) % n;
    },

    heroNext() {
      const n = this.featuredPresets.length || 1;
      this.heroIndex = (this.heroIndex + 1) % n;
    },

    clearSearch() {
      this.searchQuery = "";
      this.searchDomain = "";
      this.queueSearch(true);
    },

    jumpToFirstResult() {
      if (this.filteredPresets.length > 0) this.openPreset(this.filteredPresets[0]);
    },

    goBatch() {
      this.$router.push({ name: "BatchTask", params: { taskId: this.taskId } });
    },

    openPreset(p) {
      this.$router.push({ name: "BatchTask", params: { taskId: this.taskId }, query: { preset: p.preset_code } });
    },

    fillPromptDemo() {
      this.llmPrompt =
        "Generate a pair-end WGS analysis pipeline, use FastQC for quality control, remove standard Illumina adapters, and use the mouse reference genome..."},

    async copyText(text) {
      const t = (text ?? "").toString();
      if (!t.trim()) { alert("Nothing to copy."); return; }

      try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
          await navigator.clipboard.writeText(t);
          alert("Copied.");
          return;
        }
      } catch (e) {
        console.error("clipboard api failed, fallback to execCommand:", e);
      }

      try {
        const ta = document.createElement("textarea");
        ta.value = t;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        ta.style.top = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ta.setSelectionRange(0, ta.value.length);

        const ok = document.execCommand("copy");
        document.body.removeChild(ta);

        if (ok) alert("Copied.");
        else alert("Copy failed. Please copy manually.");
      } catch (e) {
        console.error("execCommand copy failed:", e);
        alert("Copy failed. Please copy manually.");
      }
    },

    async applyPreset() {
      const raw = (this.presetKeyword || "").trim();
      if (!raw) return;

      try {
        const res = await axios.post(`${API_BASE_URL}/api/batch_code/get_preset/`, { keyword: raw });
        if (res.status === 200) {
          this.$router.push({
            name: "BatchTask",
            params: { taskId: this.taskId },
            query: { preset: this.presetKeyword }
          });
        } else {
          // Always show an English-safe message
          this.$message.error("Failed to fetch preset.");
        }
      } catch (err) {
        console.error("applyPreset failed:", err);

        const rawMsg =
          err?.response?.data?.message ||
          err?.response?.data?.detail ||
          err?.message ||
          "";

        alert(this.sanitizeToEnglish(rawMsg, "Preset parsing failed."));
      }
    },

    buildMockPresets() {
      const img = (seed) => `https://picsum.photos/seed/${seed}/1800/980`;
      return [
        {
          id: "f1",
          featured: true,
          badge: "FEATURED",
          domain: "Genomics",
          title: "DNA/WGS • Trim → BWA → Markdup",
          desc: "A classic starter loop: fastp trimming → bwa alignment → samtools markdup. Stable, teachable, and widely applicable.",
          cover: img("science-hero-wgs-unique"),
          share_code: "p:demo_wgs_trim_bwa_markdup",
          score: 99
        },
        {
          id: "f2",
          featured: true,
          badge: "FEATURED",
          domain: "Transcriptomics",
          title: "RNA-seq • Trim → STAR → Sort/Index",
          desc: "A standard RNA-seq backbone: trim → STAR spliced alignment → BAM sort/index, ready for downstream QC and counting.",
          cover: img("science-hero-rnaseq-unique"),
          share_code: "p:demo_rnaseq_trim_star_sort",
          score: 97
        },
        {
          id: "f3",
          featured: true,
          badge: "FEATURED",
          domain: "Epigenomics",
          title: "ATAC-seq • Trim → BWA → Filter → Peaks",
          desc: "A common ATAC-seq backbone: trim → align → filter → peak calling (example). A solid starting template for reproducing papers.",
          cover: img("science-hero-atac-unique"),
          share_code: "p:demo_atac_trim_bwa_peaks",
          score: 96
        },
      ];
    },
  },
};
</script>

<style scoped src="./HomePage.css"></style>

